import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { TrendingUp, Calendar, Users, ArrowLeft, Eye, Gift } from 'lucide-react';
import { HeaderGestor } from 'components/HeaderGestor';
import { RankingVisualMode, RankingPeriod, RankingMetric, RankingParticipant, RankingAward } from 'types/ranking';
import {
  getAvailableMetrics,
  getAvailablePoints,
  getAvailableActions,
  getAvailableAwards,
  createFullRanking,
  updateFullRanking,
  getRankingById,
} from 'services/ranking.service';
import { useListPlayer } from 'hooks/player/useListPlayer';
import RankingLayoutSelector from './RankingLayoutSelector';
import ParticipantsSelector from './ParticipantsSelector';
import AwardSelector from './AwardSelector';
import './styles.scss';
import { useCreateRanking } from 'hooks/ranking/useCreateRanking';
import type { CreateRankingPayload } from 'types/ranking';

const RankingEditor: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEditMode = !!id;

  // Feature da branch feat--new-logins-and-forgot-passwords-page
  const [form, setForm] = useState<CreateRankingPayload>({
    title: "",
    description: "",
    principalType: 0,
    operation: {
      type: 0,
      achievement_type: 0,
      item: "xp",
      filters: [],
      sort: -1,
      sub: false
    },
    period: {
      type: 0,
      timeAmount: 1,
      timeScale: 6
    },
    techniques: ["GT03"],
    extra: {},
  });

  // Estados do formulário da branch develop
  const [rankingTitle, setRankingTitle] = useState('');
  const [selectedMetric, setSelectedMetric] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState<RankingPeriod>('mensal');
  const [selectedLayouts, setSelectedLayouts] = useState<RankingVisualMode[]>(['podio']);
  const [selectedParticipantIds, setSelectedParticipantIds] = useState<string[]>([]);

  // Estados para prêmios
  const [hasAward, setHasAward] = useState(false);
  const [showAward, setShowAward] = useState(false);
  const [selectedAwardId, setSelectedAwardId] = useState<string | undefined>(undefined);
  const [awards, setAwards] = useState<RankingAward[]>([]);

  // Estados para métricas dinâmicas
  const [selectedPointType, setSelectedPointType] = useState('');
  const [selectedActionType, setSelectedActionType] = useState('');
  const [pointTypes, setPointTypes] = useState<any[]>([]);
  const [actionTypes, setActionTypes] = useState<any[]>([]);

  // Estados de carregamento e dados
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [metrics, setMetrics] = useState<RankingMetric[]>([]);
  const [participants, setParticipants] = useState<RankingParticipant[]>([]);
  
  // Estado de erros
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Estado para modal de sucesso
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Hooks
  const createRanking = useCreateRanking();
  const { listPlayer } = useListPlayer();

  // Carregar métricas e participantes ao montar o componente
  useEffect(() => {
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Carregar dados do ranking em modo de edição
  useEffect(() => {
    if (isEditMode && id) {
      loadRankingData(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, id]);

  // Carregar pontos ou ações quando a métrica for selecionada
  useEffect(() => {
    const loadMetricOptions = async () => {
      if (selectedMetric === 'points') {
        try {
          const points = await getAvailablePoints();
          setPointTypes(points);
        } catch (error) {
          console.error('Erro ao carregar tipos de pontos:', error);
        }
      } else if (selectedMetric === 'tasks') {
        try {
          const actions = await getAvailableActions();
          setActionTypes(actions);
        } catch (error) {
          console.error('Erro ao carregar ações:', error);
        }
      }
    };

    loadMetricOptions();
  }, [selectedMetric]);

  const loadInitialData = async () => {
    try {
      setLoadingData(true);
      const [metricsData, participantsData, awardsData] = await Promise.all([
        getAvailableMetrics(),
        listPlayer(),
        getAvailableAwards(),
      ]);
      setMetrics(metricsData);
      
      const adaptedParticipants = participantsData.map((player: any) => ({
        _id: player._id || player.id,
        name: player.name || player.nome || '',
        email: player.email || '',
        avatar: player.image || player.avatar,
        fotoDePerfil: player.fotoDePerfil || player.foto_de_perfil,
        teams: player.teams, 
      }));
      
      setParticipants(adaptedParticipants);
      setAwards(awardsData);
    } catch (error) {
      console.error('Erro ao carregar dados iniciais:', error);
      alert('Erro ao carregar dados. Por favor, recarregue a página.');
    } finally {
      setLoadingData(false);
    }
  };

  const loadRankingData = async (rankingId: string) => {
    try {
      const ranking = await getRankingById(rankingId);
      setRankingTitle(ranking.title);
      setSelectedMetric(ranking.metric);

      // Se a métrica for points, carrega o tipo de ponto
      if (ranking.metric === 'points' && (ranking as any).pointType) {
        setSelectedPointType((ranking as any).pointType);
      }

      // Se a métrica for tasks, carrega o tipo de ação
      if (ranking.metric === 'tasks' && (ranking as any).actionType) {
        setSelectedActionType((ranking as any).actionType);
      }
      
      setSelectedPeriod(ranking.period);
      setSelectedLayouts(ranking.visuals);
      setSelectedParticipantIds(ranking.participants);

      // Carregar configuração de prêmio
      if (ranking.award) {
        setHasAward(ranking.award.hasAward);
        setShowAward(ranking.award.showAward);
        setSelectedAwardId(ranking.award.awardId);
      }
    } catch (error) {
      console.error('Erro ao carregar ranking:', error);
      alert('Erro ao carregar ranking. Redirecionando...');
      navigate('/gestao');
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!rankingTitle || !rankingTitle.trim()) {
      newErrors.rankingTitle = 'Título do ranking é obrigatório';
    }

    if (!selectedMetric) {
      newErrors.selectedMetric = 'Selecione uma métrica';
    }

    // Valida tipo de ponto se métrica for points
    if (selectedMetric === 'points' && !selectedPointType) {
      newErrors.selectedPointType = 'Selecione um tipo de ponto';
    }

    // Valida tipo de ação se métrica for tasks
    if (selectedMetric === 'tasks' && !selectedActionType) {
      newErrors.selectedActionType = 'Selecione uma ação';
    }

    if (selectedLayouts.length === 0) {
      newErrors.selectedLayouts = 'Selecione pelo menos um modelo visual';
    }

    if (selectedLayouts.length > 3) {
      newErrors.selectedLayouts = 'Selecione no máximo 3 modelos visuais';
    }

    if (selectedParticipantIds.length === 0) {
      newErrors.participants = 'Selecione pelo menos um participante';
    }

    // Valida prêmio se habilitado
    if (hasAward && !selectedAwardId) {
      newErrors.award = 'Selecione um prêmio ou desabilite a opção de prêmio';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Função da branch feat
  const handleCreateRanking = () => {
    createRanking(form as any)
      .then((response: any) => {
        navigate('/gestao');
      })
      .catch((error: any) => {
        console.error('Erro ao criar ranking:', error);
      });
  };

  // Função da branch develop
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      alert('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    try {
      setLoading(true);

      const payload: any = {
        title: rankingTitle,
        metric: selectedMetric,
        period: selectedPeriod,
        visuals: selectedLayouts,
        participants: selectedParticipantIds,
      };

      // Adiciona tipo de ponto se métrica for points
      if (selectedMetric === 'points') {
        payload.pointType = selectedPointType;
      }

      // Adiciona tipo de ação se métrica for tasks
      if (selectedMetric === 'tasks') {
        payload.actionType = selectedActionType;
      }

      // Adiciona configuração de prêmio
      if (hasAward && selectedAwardId) {
        const selectedAward = awards.find(a => a._id === selectedAwardId);
        payload.award = {
          hasAward: true,
          showAward: showAward,
          awardId: selectedAwardId,
          awardDetails: selectedAward, // Denormaliza dados do prêmio para performance
        };
      } else {
        payload.award = {
          hasAward: false,
          showAward: false,
        };
      }

      if (isEditMode && id) {
        await updateFullRanking(id, { ...payload, _id: id });
      } else {
        await createFullRanking(payload);
      }

      // Mostra modal de sucesso antes de redirecionar
      setShowSuccessModal(true);
      setTimeout(() => {
        navigate('/gestao');
      }, 2000);
    } catch (error) {
      console.error('❌ Erro ao salvar ranking:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <>
        <Helmet>
          <title>Carregando... - Rankifier</title>
        </Helmet>
        <div className="ranking-editor-page">
          <HeaderGestor />
          <div className="ranking-editor-container">
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Carregando dados...</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{isEditMode ? 'Editar Ranking' : 'Criar Ranking'} - Rankifier</title>
      </Helmet>

      <div className="ranking-editor-page">
        <HeaderGestor />

        <div className="ranking-editor-container">
          {/* Header da Página */}
          <div className="ranking-editor-page-header">
            <button 
              className="btn-back"
              onClick={() => navigate('/gestao')}
              type="button"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="page-header-content">
              <h1 className="page-title">
                {isEditMode ? 'Editar Ranking' : 'Criar novo ranking'}
              </h1>
              <p className="page-subtitle">
                Configure nome, métrica, período, layout visual, participantes e prêmios
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="ranking-editor-content">
            {/* Título do Ranking */}
            <div className="section-card">
              <div className="form-group">
                <label className="form-label">TÍTULO DO RANKING *</label>
                <input
                  type="text"
                  className={`form-input ${errors.rankingTitle ? 'error' : ''}`}
                  placeholder="Ex: Ranking de Vendas Janeiro 2025"
                  value={rankingTitle}
                  onChange={(e) => {
                    setRankingTitle(e.target.value);
                    if (errors.rankingTitle) {
                      setErrors({ ...errors, rankingTitle: '' });
                    }
                  }}
                />
                {errors.rankingTitle && (
                  <span className="error-message">{errors.rankingTitle}</span>
                )}
              </div>
            </div>

            {/* Layout de Duas Colunas - Métrica e Período */}
            <div className="two-column-layout">
              {/* Coluna Esquerda - Métrica */}
              <div className="editor-column">
                <div className="section-card">
                  <div className="section-card-header">
                    <div className="section-icon-wrapper">
                      <TrendingUp size={20} />
                    </div>
                    <h2 className="section-card-title">Métrica</h2>
                  </div>
                  <p className="section-card-description">
                    Selecione UMA métrica para avaliar o ranking
                  </p>

                  <div className="form-fields">
                    <div className="form-group">
                      <label className="form-label">MÉTRICA *</label>
                      <select 
                        className={`form-select ${errors.selectedMetric ? 'error' : ''}`}
                        value={selectedMetric}
                        onChange={(e) => {
                          setSelectedMetric(e.target.value);
                          if (errors.selectedMetric) {
                            setErrors({ ...errors, selectedMetric: '' });
                          }
                        }}
                      >
                        <option value="">Selecione uma métrica</option>
                        {metrics.map((metric) => (
                          <option key={metric.id} value={metric.id}>
                            {metric.label}
                          </option>
                        ))}
                      </select>
                      {errors.selectedMetric && (
                        <span className="error-message">{errors.selectedMetric}</span>
                      )}
                    </div>

                    {/* Campo condicional para tipo de ponto */}
                    {selectedMetric === 'points' && (
                      <div className="form-group">
                        <label className="form-label">TIPO DE PONTO *</label>
                        <select 
                          className={`form-select ${errors.selectedPointType ? 'error' : ''}`}
                          value={selectedPointType}
                          onChange={(e) => {
                            setSelectedPointType(e.target.value);
                            if (errors.selectedPointType) {
                              setErrors({ ...errors, selectedPointType: '' });
                            }
                          }}
                        >
                          <option value="">Selecione um tipo de ponto</option>
                          {pointTypes.map((point) => (
                            <option key={point._id} value={point._id}>
                              {point.category || point._id}
                            </option>
                          ))}
                        </select>
                        {errors.selectedPointType && (
                          <span className="error-message">{errors.selectedPointType}</span>
                        )}
                        <p className="field-hint">
                          Selecione o tipo de ponto que será utilizado para calcular o ranking
                        </p>
                      </div>
                    )}

                    {/* Campo condicional para tipo de ação */}
                    {selectedMetric === 'tasks' && (
                      <div className="form-group">
                        <label className="form-label">TIPO DE AÇÃO *</label>
                        <select 
                          className={`form-select ${errors.selectedActionType ? 'error' : ''}`}
                          value={selectedActionType}
                          onChange={(e) => {
                            setSelectedActionType(e.target.value);
                            if (errors.selectedActionType) {
                              setErrors({ ...errors, selectedActionType: '' });
                            }
                          }}
                        >
                          <option value="">Selecione uma ação</option>
                          {actionTypes.map((action) => (
                            <option key={action._id} value={action._id}>
                              {action.action || action._id}
                            </option>
                          ))}
                        </select>
                        {errors.selectedActionType && (
                          <span className="error-message">{errors.selectedActionType}</span>
                        )}
                        <p className="field-hint">
                          Selecione a ação que será utilizada para calcular o ranking
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Coluna Direita - Período */}
              <div className="editor-column">
                <div className="section-card">
                  <div className="section-card-header">
                    <div className="section-icon-wrapper">
                      <Calendar size={20} />
                    </div>
                    <h2 className="section-card-title">Período de Avaliação</h2>
                  </div>
                  <p className="section-card-description">
                    Configure o período de competição
                  </p>

                  <div className="form-fields">
                    <div className="form-group">
                      <label className="form-label">PERÍODO *</label>
                      <select 
                        className="form-select"
                        value={selectedPeriod}
                        onChange={(e) => setSelectedPeriod(e.target.value as RankingPeriod)}
                      >
                        <option value="semanal">Semanal</option>
                        <option value="mensal">Mensal</option>
                        <option value="anual">Anual</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Seção de Layout Visual */}
            <div className="section-card">
              <div className="section-card-header">
                <div className="section-icon-wrapper">
                  <Eye size={20} />
                </div>
                <h2 className="section-card-title">Layout Visual</h2>
              </div>
              <p className="section-card-description">
                Escolha como o ranking será exibido para os participantes
              </p>

              <RankingLayoutSelector
                selectedLayouts={selectedLayouts}
                onLayoutsChange={setSelectedLayouts}
              />

              {errors.selectedLayouts && (
                <div className="error-banner">
                  <span>{errors.selectedLayouts}</span>
                </div>
              )}
            </div>

            {/* Seção de Participantes */}
            <div className="section-card">
              <div className="section-card-header">
                <div className="section-icon-wrapper">
                  <Users size={20} />
                </div>
                <h2 className="section-card-title">Participantes *</h2>
              </div>
              <p className="section-card-description">
                Selecione os jogadores que participarão deste ranking
              </p>

              {errors.participants && (
                <div className="error-banner">
                  <span>{errors.participants}</span>
                </div>
              )}

              <ParticipantsSelector
                participants={participants}
                selectedParticipantIds={selectedParticipantIds}
                onSelectionChange={(ids) => {
                  setSelectedParticipantIds(ids);
                  if (errors.participants) {
                    setErrors({ ...errors, participants: '' });
                  }
                }}
                loading={loadingData}
              />
            </div>

            {/* Seção de Prêmios */}
            <div className="section-card">
              <div className="section-card-header">
                <div className="section-icon-wrapper">
                  <Gift size={20} />
                </div>
                <h2 className="section-card-title">Prêmio (Opcional)</h2>
              </div>
              <p className="section-card-description">
                Configure um prêmio para os vencedores deste ranking
              </p>

              {errors.award && (
                <div className="error-banner">
                  <span>{errors.award}</span>
                </div>
              )}

              <AwardSelector
                awards={awards}
                hasAward={hasAward}
                showAward={showAward}
                selectedAwardId={selectedAwardId}
                onHasAwardChange={setHasAward}
                onShowAwardChange={setShowAward}
                onAwardSelect={(awardId) => {
                  setSelectedAwardId(awardId);
                  if (errors.award) {
                    setErrors({ ...errors, award: '' });
                  }
                }}
                onAwardCreated={(newAward) => {
                  setAwards((prev) => [...prev, newAward]);
                }}
                onAwardDeleted={(awardId) => {
                  setAwards((prev) => prev.filter((award) => award._id !== awardId));
                  if (selectedAwardId === awardId) {
                    setSelectedAwardId(undefined);
                  }
                }}
                loading={loadingData}
              />
            </div>

            {/* Botões de Ação */}
            <div className="form-actions-bottom">
              <button 
                type="button" 
                className="btn-cancel-large"
                onClick={() => navigate('/gestao')}
                disabled={loading}
              >
                Cancelar
              </button>
              <button 
                type="submit" 
                className="btn-save-large"
                disabled={loading}
              >
                {loading ? 'Salvando...' : isEditMode ? 'Salvar Alterações' : 'Criar Ranking'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Modal de Sucesso */}
      {showSuccessModal && (
        <div className="success-modal-overlay">
          <div className="success-modal">
            <div className="success-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
            <h2 className="success-title">
              {isEditMode ? 'Ranking Atualizado!' : 'Ranking Criado!'}
            </h2>
            <p className="success-message">
              {isEditMode 
                ? 'As alterações foram salvas com sucesso.' 
                : 'O ranking foi criado e está pronto para uso.'}
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default RankingEditor;